export interface PasswordRules {
  /** Gets or sets the minimum length a password must be. */
  requiredLength?: number;
  /** Gets or sets a flag indicating if passwords must contain a lower case ASCII character. */
  requireLowercase?: boolean;
  /**  Gets or sets a flag indicating if passwords must contain a upper case ASCII character. */
  requireUppercase?: boolean;
  /**  Gets or sets a flag indicating if passwords must contain a non-alphanumeric character  */
  requireNonAlphanumeric?: boolean;
  /** Gets or sets a flag indicating if passwords must contain a digit. */
  requireDigit?: boolean;
}

export interface InputBlockSettings {
  required: boolean;
  useMaxLength: boolean;
  maxLength: number | null;
  multiple?: boolean;
  accept?: string;
}

export enum FilterType {
  INVENTORY_STATUSES = 'inventory_statuses',
  CATEGORIES = 'categories',
  STYLES = 'styles',
  THEMES = 'themes',
  MATERIALS = 'materials',
  CONTACTS = 'contacts',
  PERIODS = 'periods',
  TYPES = 'types',
  SORTING = 'sorting',
  COUNTRIES = 'countries',
  AVAILABILITY = 'availability'
}

export enum FilterExpandState {
  EXPAND = 'expand',
  FOLD = 'fold'
}

export enum ViewPart {
  DROP_DOWN_TOGGLE = 'dropDownToggle',
  DROP_DOWN_DOWN = 'dropDownDown',
  CART_TOGGLE = 'cartToggle',
  MAIN_PHOTO_CONTAINER = 'mainPhotoContainer',
  EMPTY_CONTAINER = 'emptyContainer',
  THUMBNAIL_BLOCK = 'thumbnailBlock',
  THUMBNAILS_SUB_CONTAINER = 'thumbnailsSubContainer',
  THUMBNAILS_CONTAINER = 'thumbnailsContainer',
  MAIN_PHOTO_BLOCK = 'mainPhotoBlock',
  PREVIEW_PHOTO_BLOCK = 'previewPhotoBlock',
  PHOTO_PREVIEW_CONTAINER = 'photoPreviewContainer',
  PHOTO_CONTAINER = 'photoContainer',
  FILTERS_CONTENT = 'filtersContent',
  RESET_CONTENT = 'resetContent',
  DROP_DOWN_TOGGLE_LANGUAGES = 'dropDownToggleLanguages',
  DROP_DOWN_DOWN_LANGUAGES = 'dropDownDownLanguages',
  DROP_DOWN_TOGGLE_CURRENCIES = 'dropDownToggleCurrencies',
  DROP_DOWN_DOWN_CURRENCIES = 'dropDownDownCurrencies',
  SLIDER_RANGE_LINE = 'slider-range-line',
  SLIDER_RANGE_INPUT = 'slider-range-input',
  SLIDER_RANGE_TITLE = 'slider-range-title',
  SLIDER_RANGE_INPUT_START = 'slider-range-input-start',
  SLIDER_RANGE_INPUT_LINE = 'slider-range-input-line',
  SLIDER_RANGE_INPUT_END = 'slider-range-input-end',
  SLIDER_RANGE_PARENT = 'slider-range-parent',
  LINE_RANGE_PARENT = 'line-range-parent',
  HEADER_TEMPLATE = 'headerTemplate',
  ITEM_TEMPLATE = 'itemTemplate',
  SORTING_TITLE = 'sortingContent1-1',
  SORTING_POSITION = 'sortingContent1',
  SORTING_CONTENT = 'sortingContent2',
  ARTWORK_DATA_FORM_PART = 'artworksDataFormPart',
  ARTWORK_DATA_UPLOAD_THUMBNAILS_CONTAINER = 'artworksDataUploadThumbnailsContainer',
  ARTWORK_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER = 'artworksDataUploadSingleThumbnailContainer',
  ARTWORK_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER_WITH_PROGRESS = 'artworksDataUploadSingleThumbnailContainerWithProgress',
  PERSONAL_DATA_FORM_PART = 'personalDataFormPart',
  PERSONAL_DATA_UPLOAD_THUMBNAILS_CONTAINER = 'personalDataUploadThumbnailsContainer',
  PERSONAL_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER = 'personalDataUploadSingleThumbnailContainer',
  PERSONAL_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER_WITH_PROGRESS = 'personalDataUploadSingleThumbnailContainerWithProgress',
  PROGRESS = 'progress',
  GROUP_CONTAINER = 'groupContainer',
  ITEM_CONTAINER = 'itemContainer',
  ITEM_NAME = 'itemName',

  // Konfigurator ceny (typ 61). Nazwy muszą się zgadzać z tym, co wpisuje
  // PriceConfiguratorTemplateFactoryService w adminfroncie — kontrakt nie jest nigdzie
  // wymuszany, więc rozjazd nazw objawi się dopiero brakiem elementu na stronie.
  PRICE_CONFIGURATOR_CONTAINER = 'priceConfiguratorContainer',
  /** Wzorzec jednej listy rozwijanej; powielany per parametr pracy. */
  PRICE_CONFIGURATOR_PARAMETER = 'priceConfiguratorParameter',
  PRICE_CONFIGURATOR_PARAMETER_LABEL = 'priceConfiguratorParameterLabel',
  /** Wzorzec jednej pozycji listy; powielany per opcja. */
  PRICE_CONFIGURATOR_OPTION = 'priceConfiguratorOption',
  /** Zamknięty wybór (trigger): kontener (border), tekst wybranej wartości, ikona strzałki. */
  PRICE_CONFIGURATOR_TRIGGER = 'priceConfiguratorTrigger',
  PRICE_CONFIGURATOR_TRIGGER_TEXT = 'priceConfiguratorTriggerText',
  PRICE_CONFIGURATOR_ARROW = 'priceConfiguratorArrow',
  /** Wrapper pojedynczego wiersza opcji (tło/border całego wiersza; wzór jak w sortowaniu). */
  PRICE_CONFIGURATOR_OPTION_ROW = 'priceConfiguratorOptionRow',
  /** Kontener rozwiniętej listy opcji (tło/obramowanie panelu). */
  PRICE_CONFIGURATOR_PANEL = 'priceConfiguratorPanel',
  PRICE_CONFIGURATOR_PRICE = 'priceConfiguratorPrice',
  PRICE_CONFIGURATOR_BUY_BUTTON = 'priceConfiguratorBuyButton',
  /** Paragraf z napisem wewnątrz przycisku zakupu (AT-9406); stare bloki mają przycisk bez tego węzła. */
  PRICE_CONFIGURATOR_BUY_BUTTON_TEXT = 'priceConfiguratorBuyButtonText',
  PRICE_CONFIGURATOR_ERROR = 'priceConfiguratorError',
  PRICE_CONFIGURATOR_ERROR_TEXT = 'priceConfiguratorErrorText',
  PRICE_CONFIGURATOR_RETRY_BUTTON = 'priceConfiguratorRetryButton',
}

export enum ViewState {
  UNKNOWN = 'unknown',
  LOGIN = 'login',
  BASIC_VIEW = 'basicView',
  PHOTO_PREVIEW_VIEW = 'photoPreviewView',
  PAGINATION_PREVIEW_VIEW = 'paginatorPreviewView',
  MAIN = 'main',
}

export enum SegmentType {
  ARROW_LEFT = 'arrowLeft',
  ARROW_RIGHT = 'arrowRight'
}


/** The animation settings */
export enum AnimationDirection {
  RIGHT_TO_LEFT = 'rightToLeft',
  LEFT_TO_RIGHT = 'leftToRight',
  TOP_TO_BOTTOM = 'topToBottom',
  BOTTOM_TO_TOP = 'bottomToTop',
  EMPTY = ''
}

export enum AnimationType {
  DEFAULT = 'default',
  FADE_IN = 'fadeIn',
  FADE_IN_LEFT = 'fadeInLeft',
  FADE_IN_RIGHT = 'fadeInRight',
  FADE_IN_DOWN = 'fadeInDown',
  FADE_IN_UP = 'fadeInUp',
  LIGHT_SPEED_IN_RIGHT = 'lightSpeedInRight',
  LIGHT_SPEED_IN_LEFT = 'lightSpeedInLeft',
  ZOOM_IN = 'zoomIn',
}

export enum PaginationType {
  STANDARD = 'standard',
  INFINITY_SCROLL = 'infinity_scroll'
}

export enum ListType {
  CLASSIC = 'classic',
  MASONRY = 'masonry'
}

export enum FiltersPosition {
  LEFT = 'left',
  TOP = 'top'
}

export enum FooterPlace {
  SCREEN_BOTTOM = 'screenBottom',
  PAGE_BOTTOM = 'pageBottom'
}

export enum Orientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal'
}

export class AnimationSettings {
  animationType: string | AnimationType;
  animationDuration: string;
  animationDirection: string | AnimationDirection;
}

export class BlockSettings {
  [key: string]: any

  elementsInRow = 1;
  paginationType: PaginationType = PaginationType.STANDARD;
  elementsPerPage = 50;
  listType?: ListType = ListType.CLASSIC;
  defaultSorting = 'id_asc';
  showFilters = false;
  filtersPosition: FiltersPosition = FiltersPosition.TOP;
  blockWidth: string;
  footerPlace: FooterPlace;
  sticky: boolean;
  hideOnScroll: boolean;
  menuOverBody: boolean;
  orientation: Orientation;
  menuType: string;
  iconType?: IconBlockType;
  tagName: string;
  showMobileViewFilters = false;

  topMobileViewFilters = false;
  // forms data
  inputType?: InputType;
  formControlName?: string;
  password?: PasswordRules;

  segmentType?: SegmentType;
  viewState?: ViewState;
  viewPart?: ViewPart;

  showThumbnails?: boolean;
  showBigPhoto?: boolean;
  controlType: FilterType;
  filterExpandState: FilterExpandState;
  inputSettings?: InputBlockSettings;
  dataSourceUrlForSelect?: string;
  availableCurrencies?: string[];
  currencyPresentation?: 'code' | 'codeAndName';

  constructor(data?: BlockSettings) {
    Object.assign(this, data);
  }
}

export type IconBlockType = 'material' | 'fontawesome';

export type InputType = 'text' | 'search' | 'email' | 'password' | 'checkbox' | 'radio' | 'lightning' | 'file' | 'date';  // | 'number'
